/**
 * VILLAGE — realism extras (2026-11-04, owner: "improve each map", "realistic").
 *
 * The layout (what goes where, and the collision contract) is pure arithmetic
 * in `villageLayout.ts`, checked offline by `agent/bench-extras-village.mjs`.
 * This file only turns those pieces into FIVE merged static meshes — one per
 * material — built once at map load:
 *   stone (photo `trim` family)  quoins, plinths, eaves, door surrounds,
 *                                buttresses, well coping
 *   wood  (photo `wood` family)  door leaves, flower boxes, well lid, crate battens
 *   iron                         lanterns, downpipes, hinges
 *   glass (emissive)             lantern panes
 *   leaf  (procedural atlas)     ivy, geraniums, grass tufts — alpha-tested
 * No per-frame work, no per-frame allocation; the renderer freezes the
 * matrices with the rest of the map.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapExtrasContext } from "../mapExtras.js";
import { surfaceMaps, worldProjectUV } from "../mapTextures.js";
import { villageLayout, type VMat, type VPiece } from "./villageLayout.js";

export const mapIds = ["village"];

// ------------------------------------------------------------ LEAF ATLAS
/** 2 x 2 atlas, 256 px cells: 0 ivy, 1 grass, 2 red geranium, 3 mixed blooms.
 *  Drawn once per session (module cache), never disposed by map teardown. */
let atlas: THREE.CanvasTexture | null = null;
function leafAtlas(): THREE.CanvasTexture {
  if (atlas) return atlas;
  const S = 256;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S * 2;
  const g = cv.getContext("2d")!;
  let seed = 1234567;
  const r = (): number => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const hsl = (h: number, s: number, l: number): string => `hsl(${h.toFixed(0)},${s.toFixed(0)}%,${l.toFixed(0)}%)`;
  const leaf = (x: number, y: number, len: number, ang: number, col: string, lobed: boolean): void => {
    g.save();
    g.translate(x, y); g.rotate(ang);
    g.fillStyle = col;
    g.beginPath();
    if (lobed) {
      // ivy: a 3-5 lobed palmate leaf
      for (let k = 0; k < 5; k++) {
        const a = -Math.PI / 2 + (k - 2) * 0.62;
        const rr = len * (k === 2 ? 1 : k % 2 ? 0.8 : 0.55);
        g.moveTo(0, 0);
        g.quadraticCurveTo(Math.cos(a - 0.35) * rr * 0.7, Math.sin(a - 0.35) * rr * 0.7, Math.cos(a) * rr, Math.sin(a) * rr);
        g.quadraticCurveTo(Math.cos(a + 0.35) * rr * 0.7, Math.sin(a + 0.35) * rr * 0.7, 0, 0);
      }
    } else {
      g.ellipse(0, -len / 2, len * 0.42, len / 2, 0, 0, Math.PI * 2);
    }
    g.fill();
    // a darker vein / shadow side
    g.strokeStyle = "rgba(20,30,10,0.35)";
    g.lineWidth = Math.max(1, len * 0.06);
    g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -len * 0.8); g.stroke();
    g.restore();
  };
  const inCell = (cx: number, cy: number, fn: (ox: number, oy: number) => void): void => {
    g.save();
    g.beginPath(); g.rect(cx * S, cy * S, S, S); g.clip();
    fn(cx * S, cy * S);
    g.restore();
  };
  // 0 — IVY: dense where the blob mask is, ragged at the edge
  inCell(0, 0, (ox, oy) => {
    g.strokeStyle = "#4a3a24"; g.lineWidth = 2;
    for (let i = 0; i < 14; i++) { // woody runners
      g.beginPath();
      let x = ox + r() * S, y = oy + S;
      g.moveTo(x, y);
      for (let k = 0; k < 8; k++) { x += (r() - 0.5) * 40; y -= S / 8; g.lineTo(x, y); }
      g.stroke();
    }
    // an irregular BLOB of lobes (a wide root mass at the bottom, climbing
    // fingers above) — a rectangular card edge is what reads as "sticker"
    const lobes: [number, number, number][] = [[0.5, 0.92, 0.42]];
    for (let k = 0; k < 9; k++) lobes.push([0.15 + r() * 0.7, 0.15 + r() * 0.7, 0.12 + r() * 0.16]);
    const inBlob = (u: number, v: number): number => {
      let m = 0;
      for (const [lu, lv, lr] of lobes) m = Math.max(m, 1 - Math.hypot(u - lu, (v - lv) * 0.8) / lr);
      return m;
    };
    for (let i = 0; i < 2600; i++) {
      const u = 0.04 + r() * 0.92, v = 0.04 + r() * 0.94;
      if (r() > Math.min(1, inBlob(u, v) * 3)) continue;
      const l = 9 + r() * 10;
      leaf(ox + u * S, oy + v * S, l, (r() - 0.5) * 1.6, hsl(95 + r() * 25, 35 + r() * 25, 16 + r() * 18), true);
    }
  });
  // 1 — GRASS: tapered blades from the bottom edge
  inCell(1, 0, (ox, oy) => {
    for (let i = 0; i < 150; i++) {
      // blades fan out from a narrow root, tallest in the middle of the clump
      const u = 0.5 + (r() - 0.5) * (0.35 + r() * 0.5);
      const x0 = ox + u * S, y0 = oy + S;
      const mid = 1 - Math.abs(u - 0.5) * 1.6;
      const hgt = S * (0.25 + r() * 0.7) * (0.5 + 0.5 * mid), lean = (u - 0.5) * 120 + (r() - 0.5) * 50, wb = 1.6 + r() * 2.4;
      g.fillStyle = hsl(52 + r() * 28, 16 + r() * 22, 20 + r() * 24); // dry, dusty village grass
      g.beginPath();
      g.moveTo(x0 - wb, y0);
      g.quadraticCurveTo(x0 + lean * 0.3, y0 - hgt * 0.6, x0 + lean, y0 - hgt);
      g.quadraticCurveTo(x0 + lean * 0.3 + wb * 0.5, y0 - hgt * 0.55, x0 + wb, y0);
      g.fill();
    }
  });
  // 2 / 3 — window-box planting: foliage below, bloom clusters on top
  const planting = (cx: number, cy: number, blooms: string[]): void => inCell(cx, cy, (ox, oy) => {
    for (let i = 0; i < 520; i++) {
      const u = 0.03 + r() * 0.94, v = 0.38 + r() * 0.6;
      leaf(ox + u * S, oy + v * S, 14 + r() * 12, (r() - 0.5) * 2.4, hsl(100 + r() * 20, 40 + r() * 20, 20 + r() * 16), false);
    }
    // trailing stems over the box front
    for (let i = 0; i < 22; i++) {
      let x = ox + r() * S, y = oy + S * 0.75;
      for (let k = 0; k < 6; k++) {
        leaf(x, y, 10 + r() * 6, (r() - 0.5) * 2, hsl(105, 40, 22 + r() * 12), false);
        x += (r() - 0.5) * 16; y += 9;
      }
    }
    for (let c = 0; c < 26; c++) {
      const bx = ox + 14 + r() * (S - 28), by = oy + 20 + r() * S * 0.42;
      const col = blooms[Math.floor(r() * blooms.length)];
      for (let k = 0; k < 16; k++) {
        const a = r() * Math.PI * 2, d = r() * 13;
        g.fillStyle = col;
        g.beginPath(); g.arc(bx + Math.cos(a) * d, by + Math.sin(a) * d * 0.8, 3 + r() * 2.5, 0, Math.PI * 2); g.fill();
      }
      g.fillStyle = "rgba(255,255,255,0.25)";
      g.beginPath(); g.arc(bx - 3, by - 4, 3, 0, Math.PI * 2); g.fill();
    }
  });
  planting(0, 1, ["#c41e24", "#d8342c", "#a8141c", "#e04a3a"]);
  planting(1, 1, ["#e86aa0", "#f4f0f2", "#b07ad8", "#f08ab8", "#ffffff"]);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.userData.shared = true; // module-cached: map teardown must not dispose it
  atlas = t;
  return t;
}

/** atlas cell → (u0, v0) of its lower-left corner (CanvasTexture flipY). */
const cellUV = (cell: number): [number, number] => [(cell % 2) * 0.5, 0.5 - Math.floor(cell / 2) * 0.5];

// ------------------------------------------------------------- GEOMETRY
const col = new THREE.Color();
function paint(g: THREE.BufferGeometry, hex: string, jitter: number): void {
  col.set(hex).multiplyScalar(jitter);
  const n = g.getAttribute("position").count;
  const c = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { c[i * 3] = col.r; c[i * 3 + 1] = col.g; c[i * 3 + 2] = col.b; }
  g.setAttribute("color", new THREE.BufferAttribute(c, 3));
}
function setCellUV(g: THREE.BufferGeometry, cell: number): void {
  const [u0, v0] = cellUV(cell);
  const uv = g.getAttribute("uv");
  // inset half a texel-ish so mips do not bleed the neighbour cell
  for (let i = 0; i < uv.count; i++) uv.setXY(i, u0 + 0.004 + uv.getX(i) * 0.492, v0 + 0.004 + uv.getY(i) * 0.492);
}
const TILE: Record<VMat, number> = { stone: 1.6, wood: 1.4, iron: 1, glass: 1, leaf: 1 };

function pieceGeo(p: VPiece): THREE.BufferGeometry[] {
  const jit = 0.9 + 0.2 * (Math.abs(Math.sin(p.x * 12.99 + p.z * 78.23 + p.y * 3.1)) % 1);
  if (p.shape === "box" || p.shape === "cyl") {
    const g = (p.shape === "box"
      ? new THREE.BoxGeometry(p.w, p.h, p.d)
      : new THREE.CylinderGeometry(p.w / 2, p.w / 2, p.h, 10)).toNonIndexed();
    g.translate(p.x, p.y, p.z);
    worldProjectUV(g, TILE[p.mat]);
    paint(g, p.color, p.mat === "iron" || p.mat === "glass" ? 1 : jit);
    return [g];
  }
  if (p.shape === "card") {
    const g = new THREE.PlaneGeometry(p.nx ? p.d : p.w, p.h).toNonIndexed();
    setCellUV(g, p.cell ?? 0);
    if (p.nx) g.rotateY(p.nx > 0 ? Math.PI / 2 : -Math.PI / 2);
    else if ((p.nz ?? 1) < 0) g.rotateY(Math.PI);
    g.translate(p.x, p.y, p.z);
    paint(g, p.color, jit);
    return [g];
  }
  // tuft: three crossed vertical cards; normals bent UP so a clump lights like
  // a mass of grass instead of three flat signs
  const out: THREE.BufferGeometry[] = [];
  for (let k = 0; k < 3; k++) {
    const g = new THREE.PlaneGeometry(p.w, p.h).toNonIndexed();
    setCellUV(g, p.cell ?? 1);
    g.rotateY((p.rot ?? 0) + (k * Math.PI) / 3);
    g.translate(p.x, p.y, p.z);
    const nrm = g.getAttribute("normal");
    for (let i = 0; i < nrm.count; i++) {
      const nx = nrm.getX(i) * 0.35, nz = nrm.getZ(i) * 0.35, l = Math.hypot(nx, 1, nz);
      nrm.setXYZ(i, nx / l, 1 / l, nz / l);
    }
    paint(g, p.color, jit);
    out.push(g);
  }
  return out;
}

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const pieces = villageLayout(ctx.map, { lowSpec: ctx.lowSpec, day: ctx.theme === "day" });
  if (!pieces.length) return null;
  const byMat = new Map<VMat, THREE.BufferGeometry[]>();
  for (const p of pieces) {
    const list = byMat.get(p.mat) ?? byMat.set(p.mat, []).get(p.mat)!;
    list.push(...pieceGeo(p));
  }
  const neon = ctx.theme === "neon";
  const mats: Record<VMat, () => THREE.Material> = {
    stone: () => new THREE.MeshStandardMaterial(surfaceMaps("trim", { color: "#ffffff", vertexColors: true, roughness: 0.92, metalness: 0 })),
    wood: () => new THREE.MeshStandardMaterial(surfaceMaps("wood", { color: "#ffffff", vertexColors: true, roughness: 0.85, metalness: 0 })),
    iron: () => new THREE.MeshStandardMaterial({ color: "#ffffff", vertexColors: true, roughness: 0.55, metalness: 0.65 }),
    glass: () => new THREE.MeshStandardMaterial({
      color: "#ffe7b5", emissive: "#ffb45c", emissiveIntensity: neon ? 2.2 : 0.55, roughness: 0.15, metalness: 0,
    }),
    leaf: () => new THREE.MeshStandardMaterial({
      map: leafAtlas(), vertexColors: true, alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.88, metalness: 0,
    }),
  };
  const group = new THREE.Group();
  group.name = "extras:village";
  for (const [mat, geos] of byMat) {
    const merged = mergeGeometries(geos);
    for (const g of geos) g.dispose();
    if (!merged) { console.error(`[village extras] merge failed for ${mat} — skipped`); continue; }
    merged.computeBoundingSphere();
    const mesh = new THREE.Mesh(merged, mats[mat]());
    mesh.name = `village:${mat}`;
    mesh.receiveShadow = true;
    mesh.castShadow = ctx.shadows && mat !== "glass" && (mat !== "leaf" || !ctx.lowSpec);
    group.add(mesh);
  }
  return group;
}
